export interface PresignedPost {
  url: string;
  fields: Record<string, string>;
}

/**
 * POST a file straight to S3 using a presigned POST policy.
 *
 * Two things are load-bearing and easy to break:
 *  - every policy field goes in the FormData FIRST, the file LAST. S3 rejects
 *    the request outright if the file precedes the policy.
 *  - no Authorization header. The bucket is public and our JWT means nothing
 *    to it; sending one fails CORS.
 *
 * XMLHttpRequest rather than fetch purely for `upload.onprogress` — these are
 * large files and the dialog shows a determinate bar.
 *
 * Success is HTTP 204 with an empty body.
 */
export function postToPresignedS3({
  presign,
  file,
  onProgress,
}: {
  presign: PresignedPost;
  file: File;
  onProgress?: (percent: number) => void;
}): Promise<void> {
  return new Promise((resolve, reject) => {
    const { url, fields } = presign || ({} as PresignedPost);
    if (!url || !fields) {
      reject(new Error("Upload could not be prepared. Please try again."));
      return;
    }

    const formData = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
      formData.append(key, value as string);
    });
    formData.append("file", file);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", url, true);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };

    xhr.onload = () => {
      if (xhr.status === 204 || xhr.status === 200) {
        resolve();
      } else {
        reject(new Error("Upload failed. Please try again."));
      }
    };

    xhr.onerror = () => reject(new Error("Upload failed. Please try again."));

    xhr.send(formData);
  });
}

export default postToPresignedS3;
